import React, { useState } from "react";
import { FiUser, FiUsers, FiCheck } from "react-icons/fi";
import { Button } from "newui/components/Button";
import type { PanelMode } from "../../types/panel.type";

interface EvaluationModeSelectorProps {
	onStart: (mode: PanelMode) => void;
	hasAIAccess: boolean;
	isLoading?: boolean;
}

interface ModeOption {
	value: PanelMode;
	icon: React.ReactNode;
	title: string;
	description: string;
}

const MODE_OPTIONS: ModeOption[] = [
	{
		value: "manual_solo",
		icon: <FiUser className="h-6 w-6" />,
		title: "Solo Evaluation",
		description: "Evaluate suppliers independently as the RFx Lead",
	},
	{
		value: "manual_panel",
		icon: <FiUsers className="h-6 w-6" />,
		title: "Panel Evaluation",
		description:
			"Invite team members to collaboratively evaluate suppliers",
	},
];

export const EvaluationModeSelector: React.FC<EvaluationModeSelectorProps> = ({
	onStart,
	hasAIAccess,
	isLoading = false,
}) => {
	const [selectedMode, setSelectedMode] = useState<PanelMode | null>(null);

	const handleStart = (): void => {
		if (selectedMode) {
			onStart(selectedMode);
		}
	};

	return (
		<div data-testid="evaluation-mode-selector" className="w-full">
			<h3 className="text-base font-medium text-gray-900 mb-4">
				Choose Evaluation Mode
			</h3>
			<div className="flex flex-row gap-4 mb-6">
				{MODE_OPTIONS.map((option) => {
					const isSelected = selectedMode === option.value;
					return (
						<button
							key={option.value}
							type="button"
							data-testid={`mode-card-${option.value}`}
							onClick={() => setSelectedMode(option.value)}
							className={`
								relative flex flex-col items-center p-6 rounded-lg border-2 cursor-pointer
								transition-all duration-150 flex-1 text-center
								${
									isSelected
										? "border-blue-500 bg-blue-50"
										: "border-gray-200 bg-white hover:border-gray-300"
								}
							`}
						>
							{isSelected && (
								<div className="absolute top-2 right-2 text-blue-500">
									<FiCheck
										className="h-5 w-5"
										data-testid={`mode-check-${option.value}`}
									/>
								</div>
							)}
							<div
								className={`mb-3 ${
									isSelected
										? "text-blue-500"
										: "text-gray-400"
								}`}
							>
								{option.icon}
							</div>
							<h4 className="text-sm font-semibold text-gray-900 mb-1">
								{option.title}
							</h4>
							<p className="text-xs text-gray-500">
								{option.description}
							</p>
							{hasAIAccess && (
								<span
									data-testid={`ai-badge-${option.value}`}
									className="mt-3 inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700"
								>
									AI-assisted
								</span>
							)}
						</button>
					);
				})}
			</div>
			<Button
				btntype="primary"
				onClick={handleStart}
				disabled={!selectedMode || isLoading}
				data-testid="start-evaluation-btn"
			>
				{isLoading ? "Starting..." : "Start Evaluation"}
			</Button>
		</div>
	);
};
